/* eslint-disable camelcase */
import React from 'react';
import {
  get,
  map,
  reduce,
  merge,
  isEmpty,
  forEach,
  find,
  filter,
  includes,
  isPlainObject
} from 'lodash';
import numeral from 'numeral';
import { Link } from 'react-router-dom';

import lookerApi from '../services/looker-api';
import {
  exportXLSXBuffer,
  exportCSV,
  formatNaturalLanguageFilters
} from './export';

export const stringSplitter = '__';
export const dimensionMinWidth = 200;
export const measureMinWidth = 150;

export const hiddenFields = ['client_crosstab.parent_content_id'];

const fieldsThatCantBeExportedWithoutLimit = [
  'client_crosstab.viewer_share_row',
  'client_crosstab.view_share_row',
  'client_crosstab.view_share_col',
  'client_crosstab.viewer_share_col',
  'client_crosstab.character_vote_share'
];

const needsLimit = fields => {
  let answer = false;
  fields.forEach(field => {
    if (fieldsThatCantBeExportedWithoutLimit.includes(field)) {
      answer = true;
    }
  });

  return answer;
};

export const ANY = 0;
export const DIMENSION = 1;
export const MEASURE = 2;

export const fieldTypes = [
  {
    label: 'All fields',
    value: ANY,
    onFilter: () => true
  },
  {
    label: 'Dimensions',
    value: DIMENSION,
    onFilter: fieldType => fieldType === DIMENSION
  },
  {
    label: 'Metrics',
    value: MEASURE,
    onFilter: fieldType => fieldType === MEASURE
  }
];

export const downloadReport = async (
  specs,
  filters,
  resultFormat,
  prefix,
  forceRawDownload
) => {
  const newSpecs = specs;
  newSpecs.resultFormat = resultFormat;
  // newSpecs.pivots = [];
  newSpecs.applyFormatting = true;
  newSpecs.limit = needsLimit(specs.fields) ? 10000 : -1;

  try {
    const response = await lookerApi.fetchCustomView(newSpecs);
    const fs = formatNaturalLanguageFilters(filters);
    if (resultFormat === 'csv') {
      exportCSV(response, fs, `${prefix}.${resultFormat}`);
    } else if (resultFormat === 'xlsx') {
      exportXLSXBuffer(
        await response.blob(),
        fs,
        `CVMi -- Report Builder`,
        `${prefix}.${resultFormat}`
      );
    }

    return true;
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const getFieldFromKey = (fields, key) => {
  const keys = key.split(stringSplitter);
  return find(fields, f => f.name.includes(keys[0]));
};

export const formatValue = (rowData, field) => {
  const value = isPlainObject(rowData) ? rowData.value : rowData;
  const { is_numeric, value_format, name } = field;
  if (value === undefined || (!value && value !== 0)) {
    return is_numeric ? '--' : 'None';
  }
  if (name === 'client_crosstab.title') {
    const {
      row: {
        original: { parent_content_id, content_type }
      }
    } = rowData;
    return parent_content_id ? (
      <Link
        to={`/${
          content_type === 'series' ? 'show' : 'movie'
        }/${parent_content_id}`}
      >
        {value}
      </Link>
    ) : (
      value
    );
  }
  if (is_numeric === false || value_format === null) return value;
  let number;
  if (value_format.includes('%')) {
    number = numeral(value / 100).format('0.00%');
    if (number.includes('NaN')) {
      return '0.00%';
    }
  } else {
    number = numeral(value).format(value_format);
    if (number.includes('NaN')) {
      return value_format;
    }
  }

  return number;
};

const renderBottomHeader = (accessor, name) => (
  <React.Fragment key={accessor}>{name}</React.Fragment>
);

const getPivotLabels = (keys, key, pivots) => {
  if (!key.includes(stringSplitter)) {
    return false;
  }

  const keyValues = keys.splice(1, keys.length);
  const final = {};
  keyValues.forEach((v, index) => {
    final[pivots[index].name] = v;
  });
  return final;
};

export const formatColumnHeaderFromField = (field, pivots) => {
  if (!field) {
    return {};
  }

  const keys = field.name.split('.');
  const key = keys[keys.length - 1];
  return {
    key,
    label: field.label,
    values: getPivotLabels(keys, key, pivots),
    isPivot: key.split(stringSplitter).length > 1,
    name: key,
    accessor: key,
    Header: () => renderBottomHeader(key, field.label),
    Cell: row => formatValue(row, field),
    className: field.fieldType === 2 ? 'measure' : 'dimension',
    minWidth: field.fieldType === 2 ? measureMinWidth : dimensionMinWidth,
    isDragDisabled: field.fieldType === 2
  };
};

const getSortType = ({ is_numeric, type }) => {
  if (is_numeric || type === 'date') {
    return 'basic';
  }

  return 'alphanumeric';
};

export const createRowTemplate = (data, fields, pivots) => {
  const dataSlice = data.slice(0, 1000);
  const uniqueKeys = dataSlice.reduce((keys, current) => {
    Object.keys(current).forEach(k => {
      if (
        !keys.includes(k) &&
        getFieldFromKey(fields, k) &&
        !hiddenFields.includes(k)
      ) {
        keys.push(k);
      }
    });
    return keys;
  }, []);

  const cols = [];

  if (!get(pivots, 'length', 0)) {
    uniqueKeys.forEach(key => {
      const field = getFieldFromKey(fields, key);

      cols.push({
        Header: field.label,
        name: key,
        id: key,
        sortType: getSortType(field),
        accessor: key,
        Cell: row => formatValue(row, field),
        className: `${
          field.is_numeric ? 'measure' : 'dimension'
        } tvt-tabular-data-table__rb-col`
      });
    });

    return cols;
  }

  cols.push({
    Header: get(pivots, [0, 'label'], ''),
    name: uniqueKeys[0],
    id: get(pivots, [0, 'name'], ''),
    columns: [],
    className: 'dimension tvt-tabular-data-table__rb-pivot-row top'
  });

  forEach(uniqueKeys, (c, colIndex) => {
    const field = getFieldFromKey(fields, c);

    if (colIndex === 0) {
      return;
    }
    const k = c.split(stringSplitter);
    cols.push({
      Header: k[1] || '',
      name: c,
      id: c,
      columns: [],
      className: `${
        field.fieldType === 2 ? 'measure' : 'dimension'
      } tvt-tabular-data-table__rb-pivot-col-th top`
    });
  });

  forEach(cols, (c, index) => {
    const key = get(uniqueKeys, index, '');
    const field = getFieldFromKey(fields, key);

    forEach(pivots, (p, i) => {
      if (i === 0) {
        return;
      }
      if (index === 0) {
        c.columns.push({
          Header: get(p, ['label'], ''),
          name: key,
          columns: [],
          className: `${
            field.fieldType === 2 ? 'measure' : 'dimension'
          } tvt-tabular-data-table__rb-pivot-col-th top`
        });
      } else {
        let level = 0;
        let columnToUpdate = false;
        while (level < i) {
          columnToUpdate = columnToUpdate
            ? columnToUpdate[0].columns
            : c.columns;
          level += 1;
        }
        const k = key.split(stringSplitter);

        columnToUpdate.push({
          Header: get(k, [i + 1], ''),
          name: key,
          id: k + k[i],
          columns: [],
          className: `${
            field.fieldType === 2 ? 'measure' : 'dimension'
          } tvt-tabular-data-table__rb-pivot-col-th top`
        });
      }
    });

    let level = 0;
    let columnToUpdate = [];
    while (level < pivots.length) {
      if (columnToUpdate.length) {
        columnToUpdate = columnToUpdate[0].columns;
      } else {
        columnToUpdate = c.columns;
      }
      level += 1;
    }
    columnToUpdate.push({
      Header: field.label,
      name: key,
      id: key,
      accessor: key,
      sortType: getSortType(field),
      Cell: row => formatValue(row, field),
      className: `${
        field.is_numeric ? 'measure' : 'dimension'
      } tvt-tabular-data-table__rb-col`
    });
  });

  return cols;
};

export const parseFields = (fields, isDimension) =>
  reduce(
    fields,
    (acc, field) =>
      field.hidden
        ? acc
        : {
            ...acc,
            [field.name]: {
              ...field,
              fieldType: isDimension ? 1 : 2,
              label: field.label_short,
              label_long: field.label,
              active: false,
              disabled: false
            }
          },
    {}
  );

export const groupFields = fields => {
  const final = [];

  forEach(fields, f => {
    const fieldView = f.view_label;
    const section = find(final, o => o.name === fieldView);

    if (section) {
      section.fields.push(f);
    } else {
      final.push({
        name: fieldView,
        label: f.view_label,
        fields: [f],
        active: false,
        open: true
      });
    }
  });

  return final;
};

export const constructFields = view => {
  const dimensions = get(view, ['fields', 'dimensions'], []);
  const measures = get(view, ['fields', 'measures'], []);
  const parsedDimensions = parseFields(dimensions, true);
  const parsedMeasures = parseFields(measures, false);

  return merge(parsedDimensions, parsedMeasures);
};

export const constructView = views => {
  const view = views[0];
  const name = 'default';
  let label;
  const dimensions = get(view, ['fields', 'dimensions'], []);
  const measures = get(view, ['fields', 'measures'], []);
  if (!isEmpty(dimensions)) {
    label = get(dimensions, [0, 'view_label'], '');
  } else if (!isEmpty(measures)) {
    label = get(measures, [0, 'view_label'], '');
  }

  const fields = constructFields(view);

  return {
    [name]: {
      name,
      label,
      fields,
      active: false,
      open: true
    }
  };
};

export const buildReportFromData = (state, data) => {
  const { uuid, fields, filters, name, pivots, userId } = data;
  let formattedFilters = [];
  if (filters) {
    formattedFilters = filters.map(s => {
      const f = s.split(':');
      if (f[0] === 'show') {
        return `title:${f[1]}`;
      }

      return s;
    });
  }

  return {
    uuid,
    values: filter(fields, f => !includes(pivots, f)),
    pivots,
    filters: formattedFilters,
    name,
    userId
  };
};

export const normalizeKey = key => {
  const keys = key.split('.');
  return keys[1];
};

const mapRow = row =>
  reduce(
    row,
    (acc, value, key) => {
      const newKey = normalizeKey(key);
      return { ...acc, [newKey]: value };
    },
    {}
  );

const mapData = data => map(data, mapRow);

export const parseViewData = rawData => mapData(rawData);

const fieldMappingExcludes = {
  'pdt_character_vote_unified.actor_name': [
    'client_crosstab.view_count',
    'client_crosstab.view_share',
    'client_crosstab.viewer_count',
    'client_crosstab.viewer_share',
    'client_crosstab.engagement_score',
    'client_crosstab.emotion_count',
    'client_crosstab.follow_count'
  ],
  'pdt_character_vote_unified.character_name': [
    'client_crosstab.view_count',
    'client_crosstab.view_share',
    'client_crosstab.viewer_count',
    'client_crosstab.viewer_share',
    'client_crosstab.engagement_score',
    'client_crosstab.emotion_count',
    'client_crosstab.follow_count'
  ],
  'client_crosstab.date': [],
  'client_crosstab.episode_first_air_date': [],
  'client_crosstab.episode_number': ['client_crosstab.follow_count'],
  'client_crosstab.episode_sort': [],
  'client_crosstab.episode_str': [],
  'client_crosstab.network_name': [],
  'client_crosstab.season_number': ['client_crosstab.follow_count'],
  'client_crosstab.tv_show_first_air_date': [],
  'client_crosstab.tv_show_name': [],
  'client_crosstab.tv_show_runtime': [],
  'client_crosstab.user_age_bucket': [],
  'client_crosstab.user_country': [],
  'client_crosstab.user_gender': [],
  'client_crosstab.character_vote_count': [
    'pdt_device_denorm.device_name',
    'pdt_genre_unified.genre',
    'pdt_emotion_vote_unified.emotion_name'
  ],
  'client_crosstab.days_from_release': [
    'pdt_device_denorm.device_name',
    'client_crosstab.emotion_count',
    'pdt_emotion_vote_unified.emotion_name'
  ],
  'client_crosstab.emotion_count': [
    'pdt_character_vote_unified.character_name'
  ],
  'client_crosstab.engagement_score': [
    'pdt_character_vote_unified.character_name'
  ],
  'client_crosstab.view_count': ['pdt_character_vote_unified.character_name'],
  'client_crosstab.view_share': ['pdt_character_vote_unified.character_name'],
  'client_crosstab.viewer_count': ['pdt_character_vote_unified.character_name'],
  'client_crosstab.viewer_share': ['pdt_character_vote_unified.character_name'],
  'pdt_emotion_vote_unified.emotion_name': [
    'client_crosstab.character_vote_count',
    'client_crosstab.follow_count'
  ],
  'pdt_genre_unified.genre': ['client_crosstab.character_vote_count'],
  'pdt_device_denorm.device_name': [
    'client_crosstab.character_vote_count',
    'client_crosstab.follow_count'
  ],
  'client_crosstab.follow_count': [
    'pdt_character_vote_unified.character_name',
    'pdt_character_vote_unified.actor_name',
    'pdt_device_denorm.device_name',
    'pdt_emotion_vote_unified.emotion_name',
    'client_crosstab.season_number',
    'client_crosstab.episode_str',
    'client_crosstab.episode_number'
  ]
};

export const isDisabledField = (fields, fieldName) => {
  let disabled = false;
  Object.keys(fields).forEach(name => {
    if (
      fields[name].active &&
      fieldMappingExcludes[name] &&
      fieldMappingExcludes[name].includes(fieldName)
    ) {
      disabled = true;
    }
  });
  return disabled;
};

export const isDisabledWithoutPivots = (pivots, f) =>
  !pivots.length &&
  (f === 'client_crosstab.viewer_share_row' ||
    f === 'client_crosstab.view_share_row');
